import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import SEO, { absoluteUrl, SITE_NAME } from '../components/SEO';
import { Clock, ArrowLeft, User, Loader2, AlertTriangle, Tag } from 'lucide-react';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton, LinkedinShareButton } from 'react-share';
import { getCategoryColor } from '../constants/categoryColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ArticleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/articles/${id}`);
      setArticle(data);

      // 1) Related articles - overlapping tags first, then same category, then any latest
      let relatedList = [];
      const seen = new Set([data.id]);
      if (data.tags && data.tags.length > 0) {
        const tagResults = await Promise.all(
          data.tags.slice(0, 3).map(t =>
            axios.get(`${API}/articles`, { params: { tag: t, limit: 6 } }).then(r => r.data).catch(() => [])
          )
        );
        for (const arr of tagResults) {
          for (const a of arr) {
            if (!seen.has(a.id)) { seen.add(a.id); relatedList.push(a); }
          }
        }
      }
      if (relatedList.length < 4) {
        const catRel = await axios.get(`${API}/articles`, {
          params: { category: data.category, limit: 6 }
        }).then(r => r.data).catch(() => []);
        for (const a of catRel) {
          if (!seen.has(a.id)) { seen.add(a.id); relatedList.push(a); }
        }
      }
      // Final fallback: pull from anywhere so Related is never empty for internal linking
      if (relatedList.length < 4) {
        const anyRel = await axios.get(`${API}/articles`, { params: { limit: 8 } })
          .then(r => r.data).catch(() => []);
        for (const a of anyRel) {
          if (!seen.has(a.id)) { seen.add(a.id); relatedList.push(a); }
        }
      }
      setRelated(relatedList.slice(0, 4));

      // 2) Latest news across all categories (excluding only the current article) —
      // strengthens internal link hierarchy on every article page. It's fine if some
      // items overlap with Related News; internal linking density on the same page
      // is not penalized by search engines.
      const latestData = await axios.get(`${API}/articles`, { params: { limit: 12 } })
        .then(r => r.data).catch(() => []);
      const latestFiltered = latestData.filter(a => a.id !== data.id).slice(0, 6);
      setLatestNews(latestFiltered);
    } catch (err) {
      setError('Article not found');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F0]" data-testid="article-loading">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 size={32} className="animate-spin text-[#E63946]" />
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-[#FFF8F0]" data-testid="article-not-found">
        <SEO title="Article Not Found" noindex />
        <Header />
        <div className="max-w-[800px] mx-auto px-6 py-24 text-center">
          <AlertTriangle size={48} className="text-[#E63946] mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-[#1A1A2E] mb-3">Article Not Found</h1>
          <p className="text-[#525252] mb-6">The article you're looking for may have been removed or never existed.</p>
          <Link
            to="/"
            data-testid="back-home-from-error"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#d32f3e]"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const color = getCategoryColor(article.category);
  // Detect whether content is HTML (from rich text editor) or plain text (legacy)
  const isHtml = /<\/?(p|h[1-6]|ul|ol|li|blockquote|img|a|strong|em|hr|br)\b/i.test(article.content);
  const plainTextForCounting = isHtml ? article.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : article.content;
  const wordCount = plainTextForCounting.split(/\s+/).filter(Boolean).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  // SEO: build description from first ~160 chars of plain content
  const seoDescription = plainTextForCounting.slice(0, 160).trim() + (plainTextForCounting.length > 160 ? '…' : '');
  const articleUrl = absoluteUrl(`/article/${article.id}`);
  const articleImage = absoluteUrl(article.image_url);
  const newsArticleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    description: seoDescription,
    image: [articleImage],
    datePublished: article.published_at,
    dateModified: article.published_at,
    author: [{ '@type': 'Person', name: article.author }],
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: absoluteUrl('/logo.jpeg') },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
    articleSection: color.name,
    keywords: (article.tags || []).join(', '),
    articleBody: plainTextForCounting.slice(0, 5000),
    wordCount: wordCount,
    inLanguage: 'en-IN',
    isAccessibleForFree: true,
    url: articleUrl,
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrl('/') },
      { '@type': 'ListItem', position: 2, name: color.name, item: absoluteUrl(`/?category=${article.category}`) },
      { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
    ],
  };

  // For legacy plain-text content: split into paragraphs (by double newline or every ~3 sentences)
  const paragraphs = !isHtml ? article.content.split(/\n\n+/).filter(p => p.trim().length > 0) : [];
  const displayParagraphs = !isHtml
    ? (paragraphs.length > 1
        ? paragraphs
        : article.content.split(/(?<=[.!?])\s+(?=[A-Z])/).reduce((acc, sentence, i) => {
            const groupIdx = Math.floor(i / 3);
            if (!acc[groupIdx]) acc[groupIdx] = '';
            acc[groupIdx] += (acc[groupIdx] ? ' ' : '') + sentence;
            return acc;
          }, []))
    : [];

  const ShareRow = ({ testIdSuffix }) => (
    <div
      className="flex flex-wrap items-center gap-3 border-y border-[#E5E5E5] py-4 my-6"
      data-testid={`article-share-row-${testIdSuffix}`}
    >
      <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A]">SHARE:</span>
      <FacebookShareButton url={shareUrl} quote={article.title}>
        <div className="px-3 py-1.5 bg-[#1877F2] text-white text-xs font-bold hover:opacity-80 transition-opacity">Facebook</div>
      </FacebookShareButton>
      <TwitterShareButton url={shareUrl} title={article.title}>
        <div className="px-3 py-1.5 bg-[#1DA1F2] text-white text-xs font-bold hover:opacity-80 transition-opacity">Twitter</div>
      </TwitterShareButton>
      <WhatsappShareButton url={shareUrl} title={article.title}>
        <div className="px-3 py-1.5 bg-[#25D366] text-white text-xs font-bold hover:opacity-80 transition-opacity">WhatsApp</div>
      </WhatsappShareButton>
      <LinkedinShareButton url={shareUrl} title={article.title}>
        <div className="px-3 py-1.5 bg-[#0A66C2] text-white text-xs font-bold hover:opacity-80 transition-opacity">LinkedIn</div>
      </LinkedinShareButton>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="article-detail-page">
      <SEO
        title={article.title}
        description={seoDescription}
        path={`/article/${article.id}`}
        type="article"
        image={article.image_url}
        publishedTime={article.published_at}
        author={article.author}
        section={color.name}
        tags={article.tags || []}
        jsonLd={[newsArticleJsonLd, breadcrumbJsonLd]}
      />
      <Header />

      <div className="max-w-[1600px] mx-auto px-6 pt-6">
        <AdSlot slot="top" height={90} />
      </div>

      <article className="max-w-[860px] mx-auto px-6 py-12">
        <div className="mb-6">
          <Link
            to="/"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] hover:text-[#E63946] transition-colors"
          >
            <ArrowLeft size={14} />
            Back to Home
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span
            data-testid="article-category-badge"
            className="px-3 py-1 text-xs font-bold tracking-[0.2em] uppercase"
            style={{ backgroundColor: color.bg, color: color.text }}
          >
            {color.name}
          </span>
          {article.featured && (
            <span
              data-testid="article-breaking-badge"
              className="px-3 py-1 text-xs font-bold tracking-[0.2em] uppercase bg-[#FFB703] text-[#1A1A2E]"
            >
              ★ BREAKING
            </span>
          )}
        </div>

        <h1
          data-testid="article-title"
          className="text-4xl sm:text-5xl tracking-tight leading-tight font-black text-[#1A1A2E] mb-5"
        >
          {article.title}
        </h1>

        <div
          data-testid="article-meta"
          className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#525252] mb-8"
        >
          <span className="inline-flex items-center gap-1.5">
            <User size={14} />
            <span className="font-semibold">{article.author}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock size={14} />
            {new Date(article.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: color.bg }}
            ></span>
            {readingTime} MIN READ
          </span>
        </div>

        {article.tags && article.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="article-tags-row">
            <Tag size={14} className="text-[#8A8A8A]" />
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/tag/${encodeURIComponent(tag)}`}
                data-testid={`article-tag-${tag}`}
                className="inline-block px-3 py-1 text-xs font-bold tracking-[0.1em] uppercase border border-[#E5E5E5] hover:border-[#E63946] hover:text-[#E63946] transition-colors"
                style={{ color: color.bg }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = color.bg; e.currentTarget.style.backgroundColor = color.tint; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E5E5'; e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}

        <div className="mb-8 overflow-hidden border border-[#E5E5E5]">
          <img
            src={article.image_url}
            alt={article.title}
            data-testid="article-hero-image"
            className="w-full max-h-[520px] object-cover"
          />
        </div>

        <ShareRow testIdSuffix="top" />

        <div data-testid="article-content" className="article-body text-[#1A1A2E]">
          {isHtml ? (
            <div dangerouslySetInnerHTML={{ __html: article.content }} />
          ) : (
            displayParagraphs.map((para, i) => (
              <p key={i} className="text-lg leading-[1.8] mb-5">
                {i === 0 ? (
                  <>
                    <span
                      className="float-left text-6xl font-black leading-none mr-2 mt-1"
                      style={{ color: color.bg }}
                    >
                      {para.charAt(0)}
                    </span>
                    {para.slice(1)}
                  </>
                ) : (
                  para
                )}
              </p>
            ))
          )}
        </div>

        <ShareRow testIdSuffix="bottom" />

        <div className="my-8">
          <AdSlot slot="inline" height={250} />
        </div>

        {related.length > 0 && (
          <section className="mt-12 pt-8 border-t-4" style={{ borderColor: color.bg }} data-testid="related-articles">
            <div className="flex items-center gap-3 mb-6">
              <span
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: color.bg }}
              ></span>
              <h2 className="text-2xl font-bold text-[#1A1A2E] tracking-tight">
                {article.tags && article.tags.length > 0
                  ? `RELATED STORIES`
                  : `MORE FROM ${color.name.toUpperCase()}`}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {related.map((r, idx) => {
                const rc = getCategoryColor(r.category);
                return (
                  <Link
                    key={r.id}
                    to={`/article/${r.id}`}
                    data-testid={`related-article-${idx}`}
                    className="group border border-[#E5E5E5] bg-white overflow-hidden hover:border-[#E63946] transition-colors"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={r.image_url}
                        alt={r.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span
                        className="inline-block px-2 py-0.5 text-[10px] font-bold tracking-[0.2em] uppercase mb-2"
                        style={{ backgroundColor: rc.tint, color: rc.bg }}
                      >
                        {r.author}
                      </span>
                      <h3 className="text-base font-semibold text-[#1A1A2E] leading-snug group-hover:text-[#E63946] transition-colors">
                        {r.title}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {latestNews.length > 0 && (
          <section className="mt-12 pt-8 border-t-2 border-[#E5E5E5]" data-testid="latest-news">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#E63946] animate-pulse"></span>
              <h2 className="text-2xl font-bold text-[#1A1A2E] tracking-tight">
                LATEST NEWS
              </h2>
              <Link
                to="/"
                data-testid="latest-news-view-all"
                className="ml-auto text-xs tracking-[0.15em] uppercase font-bold text-[#E63946] hover:underline"
              >
                View All →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {latestNews.map((n, idx) => {
                const nc = getCategoryColor(n.category);
                return (
                  <Link
                    key={n.id}
                    to={`/article/${n.id}`}
                    data-testid={`latest-news-item-${idx}`}
                    className="group flex gap-3 p-3 border border-[#E5E5E5] bg-white hover:border-[#E63946] transition-colors"
                  >
                    <div className="w-20 h-20 flex-shrink-0 overflow-hidden">
                      <img
                        src={n.image_url}
                        alt={n.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span
                        className="inline-block px-1.5 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase mb-1"
                        style={{ backgroundColor: nc.tint, color: nc.bg }}
                      >
                        {nc.name}
                      </span>
                      <h3 className="text-sm font-semibold text-[#1A1A2E] leading-tight line-clamp-3 group-hover:text-[#E63946] transition-colors">
                        {n.title}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </article>

      <div className="max-w-[1600px] mx-auto px-6 pb-6">
        <AdSlot slot="bottom" height={90} />
      </div>

      <Footer />
    </div>
  );
}

export default ArticleDetailPage;
